/**
 * `ConfigClient` — gateway tipado para o config-service (ADR-0069).
 *
 * **Lifecycle (§4.5):** extends `DisposableBase`. `start()` carrega cache +
 * dispara refresh em background (não bloqueante) + agenda revalidate via
 * `setInterval(unref)`. `dispose()` clear timer + completa observable.
 *
 * **Stale tolerance:** cache > 30 dias força `DEFAULT_CONFIG`.
 * 7-30 dias loga warn mas continua usando.
 *
 * **Grandfathering:** `getEffectiveTier()` opcional permite que tier do
 * license JWT sobrescreva tier do construtor — cliente com tier preservado vê
 * o preço comprado, não o tier vigente.
 */

import {
  type AppError,
  aborted,
  asAppError,
  createLogger,
  DisposableBase,
  err,
  ok,
  parseOrErr,
  type Result,
  toDisposable,
} from '@planuze/kernel';
import { BehaviorSubject, type Observable } from 'rxjs';
import {
  DEFAULT_CONFIG,
  type PricingTier,
  type RemoteConfig,
  RemoteConfigSchema,
} from '../schema/schema.js';
import { checkStaleness } from '../stale/stale-check.js';

const log = createLogger('config.client');
const AUTHORIZATION_HEADER = 'Authorization';

function configUrlWithTier(endpoint: string, tier: PricingTier): string {
  try {
    const url = new URL(endpoint);
    url.searchParams.set('tier', tier);
    return url.toString();
  } catch {
    const separator =
      endpoint.endsWith('?') || endpoint.endsWith('&') ? '' : endpoint.includes('?') ? '&' : '?';
    return `${endpoint}${separator}tier=${encodeURIComponent(tier)}`;
  }
}

export interface ConfigClientOptions {
  readonly endpoint: string;
  readonly tier: PricingTier;
  readonly cacheGet: () => Promise<RemoteConfig | undefined>;
  readonly cacheSet: (cfg: RemoteConfig) => Promise<void>;
  readonly authToken?: () => string | undefined;
  /** Sobrescreve `tier` na URL do refresh (grandfathering via license JWT). */
  readonly getEffectiveTier?: () => PricingTier | undefined;
  /** Injetável para testes — defaults to `globalThis.fetch`. */
  readonly fetchImpl?: typeof fetch;
  /** Injetável para testes — defaults to `Date.now`. */
  readonly nowMs?: () => number;
}

export class ConfigClient extends DisposableBase {
  private current: RemoteConfig = DEFAULT_CONFIG;
  private readonly stateSubject = new BehaviorSubject<RemoteConfig>(DEFAULT_CONFIG);
  /** Observable read-only — caller não pode chamar `.next()` e quebrar o invariant. */
  readonly state$: Observable<RemoteConfig> = this.stateSubject.asObservable();
  private timer: ReturnType<typeof setInterval> | undefined;
  private currentIntervalMs = 0;
  /* Memoiza a Promise de `start()` para idempotência sob chamadas concorrentes:
     o guard de `this.timer` só fica válido depois do `await cacheGet()`, então
     duas chamadas não-awaited passariam o guard e criariam dois timers, vazando
     o primeiro (DisposableBase só limpa o último). */
  private startPromise: Promise<void> | undefined;

  constructor(private readonly opts: ConfigClientOptions) {
    super();
    this._register(
      toDisposable(() => {
        if (this.timer !== undefined) {
          clearInterval(this.timer);
          this.timer = undefined;
        }
        this.stateSubject.complete();
      }),
    );
  }

  start(): Promise<void> {
    if (this.isDisposed) return Promise.resolve();
    if (this.startPromise !== undefined) return this.startPromise;
    this.startPromise = this.runStart();
    return this.startPromise;
  }

  private async runStart(): Promise<void> {
    const cached = await this.opts.cacheGet();
    if (this.isDisposed) return;
    if (cached !== undefined) {
      const status = checkStaleness(cached, this.opts.nowMs?.() ?? Date.now());
      if (status === 'critical') {
        log.warn('config.client.critical_cache_using_default');
        this.current = DEFAULT_CONFIG;
      } else {
        this.current = cached;
      }
      this.stateSubject.next(this.current);
    }

    this.refresh().catch((e: unknown) =>
      log.error('config.client.initial_refresh_failed', { err: e }),
    );

    this.scheduleRefresh(this.current.intervals.configRefreshMs);
  }

  private scheduleRefresh(intervalMs: number): void {
    if (this.timer !== undefined && this.currentIntervalMs === intervalMs) return;
    if (this.timer !== undefined) clearInterval(this.timer);
    this.currentIntervalMs = intervalMs;
    this.timer = setInterval(() => {
      this.refresh().catch((e: unknown) =>
        log.error('config.client.scheduled_refresh_failed', { err: e }),
      );
    }, intervalMs);
    if (this.timer.unref !== undefined) this.timer.unref();
  }

  async refresh(): Promise<Result<RemoteConfig, AppError>> {
    if (this.isDisposed) return err(aborted('config_client_disposed'));
    const tier = this.opts.getEffectiveTier?.() ?? this.opts.tier;
    const headers: Record<string, string> = {};
    const token = this.opts.authToken?.();
    if (token !== undefined) headers[AUTHORIZATION_HEADER] = `Bearer ${token}`;

    const fetchFn = this.opts.fetchImpl ?? fetch;
    let res: Response;
    try {
      res = await fetchFn(configUrlWithTier(this.opts.endpoint, tier), { headers });
    } catch (e) {
      log.warn('config.client.fetch_failed', { err: e });
      return err(asAppError(e));
    }

    if (!res.ok) {
      log.warn('config.client.refresh_failed', { status: res.status });
      return err(asAppError(new Error(`http_${res.status}`)));
    }

    let json: unknown;
    try {
      json = await res.json();
    } catch (e) {
      return err(asAppError(e));
    }

    const parsed = parseOrErr(RemoteConfigSchema, json);
    if (parsed.isErr()) {
      log.error('config.client.remote_config_invalid', { err: parsed.error.message });
      return err(asAppError(parsed.error));
    }

    /* Guard pós-await (§4.5): dispose() pode ter sido chamado enquanto o
       fetch/json estava em flight. Sem isto, um refresh tardio publicaria
       state e cache depois do shutdown — espelha o guard de runStart(). */
    if (this.isDisposed) return err(asAppError(new Error('disposed')));

    try {
      await this.opts.cacheSet(parsed.value);
    } catch (e) {
      log.warn('config.client.cache_persist_failed', { err: e });
      return err(asAppError(e));
    }

    if (this.isDisposed) return err(asAppError(new Error('disposed')));

    this.current = parsed.value;
    this.stateSubject.next(parsed.value);
    /* Re-agenda o timer se o intervalo mudou no remote — sem isso, mudanças
       de cadência só viam efeito depois de reboot. */
    if (this.timer !== undefined) {
      this.scheduleRefresh(parsed.value.intervals.configRefreshMs);
    }
    log.info('config.client.updated', { generatedAt: parsed.value.generatedAt });
    return ok(parsed.value);
  }

  /** Snapshot síncrono. */
  getConfig(): RemoteConfig {
    return this.current;
  }

  /**
   * Acesso typed por dot-path. Ex: `client.get<number>('pricing.pro.monthlyBrl')`.
   *
   * Path inexistente ou que cruza um valor leaf retorna `undefined` (cast para
   * `T`) em vez de lançar — o caller declara `T` e é responsável por o path
   * apontar para o tipo correto.
   */
  get<T>(path: string): T {
    const parts = path.split('.');
    let cur: unknown = this.current;
    for (const p of parts) {
      if (cur === null || typeof cur !== 'object') return undefined as T;
      cur = (cur as Record<string, unknown>)[p];
    }
    return cur as T;
  }
}
